import React, { useEffect, useRef } from 'react';

const ResponsePanel = ({ terminalLogs = [] }) => {
  const terminalEndRef = useRef(null);
  const terminalRef = useRef(null);

  // Auto-scroll para o final quando novas mensagens chegarem
  useEffect(() => {
    if (terminalRef.current && terminalEndRef.current) {
      // Faz scroll apenas dentro do container do terminal
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminalLogs]);

  const getMessageColor = (type) => {
    switch (type) {
      case 'command':
        return '#60a5fa'; // Azul
      case 'success':
        return '#34d399'; // Verde
      case 'error':
        return '#f87171'; // Vermelho
      case 'warning':
        return '#fbbf24'; // Amarelo
      case 'info':
        return '#a78bfa'; // Roxo
      default:
        return '#cbd5e1'; // Cinza claro
    }
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'command':
        return '▶';
      case 'success':
        return '✓';
      case 'error':
        return '✗';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return '●';
    }
  };

  return (
    <div style={{ height: '100%' }}>
      <div
        ref={terminalRef}
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          border: '2px solid rgba(71, 85, 105, 0.5)',
          borderRadius: '8px',
          padding: '16px',
          height: '100%',
          overflowY: 'auto',
          fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
          fontSize: '0.85rem',
          boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.3)',
          position: 'relative'
        }}
      >
        {/* Header do terminal */}
        <div
          style={{
            position: 'sticky',
            top: '-16px',
            left: '-16px',
            right: '-16px',
            background: 'rgba(15, 23, 42, 0.95)',
            padding: '8px 16px',
            marginBottom: '12px',
            borderBottom: '1px solid rgba(71, 85, 105, 0.5)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backdropFilter: 'blur(8px)',
            marginLeft: '-16px',
            marginRight: '-16px',
            marginTop: '-16px',
            zIndex: 10
          }}
        >
          <div style={{ display: 'flex', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ef4444' }} />
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#f59e0b' }} />
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#22c55e' }} />
          </div>
          <div style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>
            TERMINAL DE COMUNICAÇÃO
          </div>
          <div
            style={{
              marginLeft: 'auto',
              color: '#475569',
              fontSize: '0.7rem',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <span style={{ color: '#22c55e', animation: 'blink 1.5s infinite' }}>●</span>
            {terminalLogs.length} mensagens
          </div>
        </div>

        {/* Conteúdo do terminal */}
        <div style={{ paddingTop: '8px' }}>
          {terminalLogs.length === 0 ? (
            <div style={{ color: '#475569', fontStyle: 'italic', textAlign: 'center', paddingTop: '50px' }}>
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📡</div>
              <div>Aguardando comandos e respostas...</div>
            </div>
          ) : (
            terminalLogs.map((log, index) => (
              <div
                key={index}
                style={{
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  animation: 'fadeIn 0.3s ease-out',
                  lineHeight: '1.5'
                }}
              >
                {/* Timestamp */}
                <span
                  style={{
                    color: '#64748b',
                    fontSize: '0.75rem',
                    minWidth: '80px',
                    flexShrink: 0
                  }}
                >
                  [{log.timestamp}]
                </span>

                {/* Ícone do tipo */}
                <span
                  style={{
                    color: getMessageColor(log.type),
                    fontWeight: 'bold',
                    minWidth: '16px',
                    flexShrink: 0
                  }}
                >
                  {getMessageIcon(log.type)}
                </span>

                {/* Tipo da mensagem */}
                <span
                  style={{
                    color: getMessageColor(log.type),
                    fontWeight: 600,
                    minWidth: '80px',
                    flexShrink: 0,
                    fontSize: '0.8rem',
                    textTransform: 'uppercase'
                  }}
                >
                  {log.label}:
                </span>

                {/* Mensagem */}
                <span
                  style={{
                    color: '#e2e8f0',
                    flex: 1,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {log.message}
                </span>
              </div>
            ))
          )}
          {/* Elemento invisível para scroll */}
          <div ref={terminalEndRef} />
        </div>

        {/* Scrollbar customizada */}
        <style>{`
          div::-webkit-scrollbar {
            width: 8px;
          }
          div::-webkit-scrollbar-track {
            background: rgba(15, 23, 42, 0.5);
            border-radius: 4px;
          }
          div::-webkit-scrollbar-thumb {
            background: rgba(71, 85, 105, 0.8);
            border-radius: 4px;
          }
          div::-webkit-scrollbar-thumb:hover {
            background: rgba(100, 116, 139, 0.9);
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateX(-10px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ResponsePanel;
